import { InfoCircleOutlined, NodeIndexOutlined } from '@ant-design/icons'
import type { components } from '@fluencelabs/vodopad-admin-api'
import { useCustom } from '@refinedev/core'
import { Breadcrumb, Card, Descriptions, Table, Tabs, Typography } from 'antd'
import { useParams, useSearchParams } from 'react-router-dom'
import { Link } from 'react-router-dom'

import { ClusterNameLink } from '@/components/common/ClusterNameLink'
import { FormattedDate } from '@/components/common/FormattedDate'
import { MONO_STYLE } from '@/components/common/MonoId'
import { RecordFallback } from '@/components/common/RecordFallback'
import { StatusTag } from '@/components/common/StatusTag'
import { TroubleshootCard } from '@/components/common/TroubleshootCard'
import { UserEmailLink } from '@/components/common/UserEmailLink'
import { VODOPAD_ENDPOINT } from '@/constants/constants'
import { useAdminRecord } from '@/hooks/useAdminRecord'
import type { PaginatedItems } from '@/types/api'

type UserVpcDto = components['schemas']['UserVpcDto']
type UserSubnetDto = components['schemas']['UserSubnetDto']

export default function VpcShowPage() {
  const { id: vpcId } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') ?? 'info'

  const { record: vpc, query: vpcQuery } = useAdminRecord<UserVpcDto>(
    'vpcs',
    vpcId,
  )

  if (!vpcId) return null
  if (!vpc)
    return (
      <RecordFallback
        label="VPC"
        isLoading={vpcQuery.isLoading}
        error={vpcQuery.error}
      />
    )

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[{ title: <Link to="/vpcs">VPCs</Link> }, { title: vpc.name }]}
      />
      <h2 style={{ marginBottom: 24 }}>
        VPC: <Typography.Text copyable>{vpc.name}</Typography.Text>
      </h2>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setSearchParams({ tab: key }, { replace: true })}
        items={[
          {
            key: 'info',
            label: 'Info',
            icon: <InfoCircleOutlined />,
            children: (
              <div style={{ display: 'grid', gap: 24 }}>
                <Card>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="ID">
                      <Typography.Text copyable style={MONO_STYLE}>
                        {vpc.id}
                      </Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Name">
                      <Typography.Text copyable>{vpc.name}</Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <StatusTag status={vpc.status} />
                    </Descriptions.Item>
                    <Descriptions.Item label="User">
                      <UserEmailLink userId={vpc.userId} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Cluster">
                      <ClusterNameLink clusterId={vpc.clusterId} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Subnets">
                      {vpc.subnetsCount}
                    </Descriptions.Item>
                    <Descriptions.Item label="External">
                      {vpc.enableExternal ? 'Yes' : 'No'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Created">
                      <FormattedDate date={vpc.createdAt} fmt="PPp" />
                    </Descriptions.Item>
                    <Descriptions.Item label="Ready Since">
                      {vpc.readySince ? (
                        <FormattedDate date={vpc.readySince} fmt="PPp" />
                      ) : (
                        '—'
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Removed">
                      <FormattedDate date={vpc.removedAt} fmt="PPp" />
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
                <TroubleshootCard
                  kind="vpc"
                  userId={vpc.userId}
                  resourceId={vpc.id}
                />
              </div>
            ),
          },
          {
            key: 'subnets',
            label: `Subnets (${vpc.subnetsCount})`,
            icon: <NodeIndexOutlined />,
            children: <VpcSubnetsTab vpcId={vpcId} />,
          },
        ]}
      />
    </div>
  )
}

function VpcSubnetsTab({ vpcId }: { vpcId: string }) {
  const subnetsQuery = useCustom<PaginatedItems<UserSubnetDto>>({
    url: `${VODOPAD_ENDPOINT}/v1/admin/subnets`,
    method: 'get',
    config: { query: { vpcId, page: 1, perPage: 50 } },
  })

  const subnets =
    (subnetsQuery.query.data?.data as PaginatedItems<UserSubnetDto> | undefined)
      ?.items ?? []

  return (
    <Table
      loading={subnetsQuery.query.isLoading}
      dataSource={subnets}
      rowKey="id"
      pagination={{ pageSize: 10 }}
      columns={[
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
          render: (name: string, record: UserSubnetDto) => (
            <Link to={`/subnets/${record.id}`}>
              <Typography.Text copyable={{ text: record.id }}>
                {name}
              </Typography.Text>
            </Link>
          ),
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          width: 100,
          render: (s: string) => <StatusTag status={s} />,
        },
        {
          title: 'IPv4 CIDR',
          dataIndex: 'ipv4Cidr',
          key: 'ipv4Cidr',
          render: (v: string | null) =>
            v ? <Typography.Text copyable>{v}</Typography.Text> : '—',
        },
        {
          title: 'IPv6 CIDR',
          dataIndex: 'ipv6Cidr',
          key: 'ipv6Cidr',
          render: (v: string | null) =>
            v ? <Typography.Text copyable>{v}</Typography.Text> : '—',
        },
      ]}
    />
  )
}
